import { css, useTheme } from '@emotion/react';
import { formatDuration, TONE_COLORS } from '@sonematic/shared';
import type { Tone } from '@sonematic/shared';
import { useState } from 'react';

import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

export interface TimelineSection {
  position: number;
  title: string;
  durationMs: number;
  tone?: string | null;
}

export interface TimelineVisualizationProps {
  sections: TimelineSection[];
  totalDurationMs: number;
  onSectionClick?: (position: number) => void;
  timingEstimated?: boolean;
}

const SECTION_COLORS = [
  '#6a5fc1',
  '#79628c',
  '#422082',
  '#c2ef4e',
  '#ffb287',
  '#fa7faa',
  '#7b71d1',
  '#584674',
];

function getSectionColor(index: number, tone?: string | null): string {
  if (tone && TONE_COLORS[tone as Tone]) return TONE_COLORS[tone as Tone].bg;
  return SECTION_COLORS[index % SECTION_COLORS.length];
}

export function TimelineVisualization({
  sections,
  totalDurationMs: _totalDurationMs,
  onSectionClick,
  timingEstimated,
}: TimelineVisualizationProps) {
  const theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (sections.length === 0) {
    return (
      <div
        css={css`
          padding: ${spacing.md};
          text-align: center;
          color: ${theme.colors.textSecondary};
          font-size: ${fontSizes.sm};
        `}
      >
        No sections to display
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing.sm};
      `}
    >
      <div
        css={css`
          flex: 1;
          display: flex;
          height: 40px;
          border-radius: ${radii.sm};
          overflow: hidden;
          position: relative;
        `}
      >
        {sections.map((section, index) => (
          <div
            key={section.position}
            role="button"
            tabIndex={0}
            aria-label={`${section.title} — ${formatDuration(section.durationMs)}`}
            data-testid={`timeline-segment-${index}`}
            style={{
              flexGrow: section.durationMs,
              backgroundColor: getSectionColor(index, section.tone),
            }}
            css={css`
              position: relative;
              flex-shrink: 0;
              min-width: 40px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              transition: opacity 0.15s ease;

              &:hover,
              &:focus-visible {
                opacity: 0.85;
              }

              &:focus-visible {
                outline: 2px solid ${theme.colors.text};
                outline-offset: -2px;
              }
            `}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onClick={() => onSectionClick?.(section.position)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSectionClick?.(section.position);
              }
            }}
          >
            <span
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                line-height: 1.2;
                overflow: hidden;
                padding: 0 ${spacing.xs};
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
              `}
            >
              <span
                css={css`
                  font-size: ${fontSizes.xs};
                  color: ${theme.colors.text};
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  font-weight: 500;
                  max-width: 100%;
                `}
              >
                {section.title}
              </span>
              <span
                css={css`
                  font-size: 10px;
                  color: rgba(255, 255, 255, 0.8);
                  white-space: nowrap;
                `}
              >
                {timingEstimated ? '~' : ''}
                {formatDuration(section.durationMs)}
              </span>
            </span>
            {hoveredIndex === index && (
              <div
                role="tooltip"
                css={css`
                  position: absolute;
                  bottom: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                  margin-bottom: ${spacing.xs};
                  background: ${theme.colors.text};
                  color: ${theme.colors.background};
                  padding: ${spacing.xs} ${spacing.sm};
                  border-radius: 4px;
                  font-size: ${fontSizes.xs};
                  white-space: nowrap;
                  z-index: 10;
                  pointer-events: none;
                  box-shadow: ${shadows.surface};
                `}
              >
                {section.title} — {formatDuration(section.durationMs)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
